(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-stretch-calculator]';
  const initializedRoots = window.CycloneStretchCalculatorRoots || new WeakSet();
  window.CycloneStretchCalculatorRoots = initializedRoots;
  const variantIdBySku = Object.create(null);
  const GATE_SIZES = [4, 5, 6, 8, 10, 12];

  const PRODUCT_CONFIG = {
    vinyl: {
      familyLabel: 'Vinyl',
      materialLabel: 'Savanna white full privacy',
      heights: [6],
      defaultHeight: 6,
      defaultPanelLength: 8,
      defaultPostDepth: 3,
      gateSizes: [4, 5, 6, 8, 10, 12]
    },
    ornamental: {
      familyLabel: 'Ornamental steel',
      materialLabel: 'Black flat top 3-rail',
      heights: [4, 5],
      defaultHeight: 4,
      defaultPanelLength: 8,
      defaultPostDepth: 4,
      gateSizes: [4, 5, 8, 10]
    }
  };

  const SKU_MAP = {
    'ORN:PANEL:4:8': 'osp4',
    'ORN:PANEL:5:8': 'osp5',
    'ORN:POSTS:8': 'os28',
    'ORN:POSTS:9': 'os29',
    'ORN:BRACKET:LINE': 'oslc',
    'ORN:BRACKET:UNIVERSAL': 'osub',
    'ORN:GATE:SINGLE:4': { 4: 'og44', 5: 'og54' },
    'ORN:GATE:SINGLE:5': { 4: 'og45', 5: 'og55' },
    'VINYL:PANEL:8': 'vp86',
    'VINYL:POST:LINE:9': 'vlp9',
    'VINYL:POST:END:9': 'vep9',
    'VINYL:POST:CORNER:9': 'vcp9',
    'VINYL:GATE:STIFFENER': 'vps9',
    'VINYL:GATE:SINGLE:4': 'vg64',
    'VINYL:GATE:SINGLE:5': 'vg65',
    'VINYL:GATE:SINGLE:6': 'vg66',
    'VINYL:CAP:EXTERIOR': 'vcw5'
  };

  function createId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return prefix + '-' + window.crypto.randomUUID();
    }
    return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function finiteNumber(value) {
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function positiveNumber(value) {
    const number = finiteNumber(value);
    return number > 0 ? number : 0;
  }

  function nonNegativeInteger(value) {
    return Math.max(0, Math.floor(finiteNumber(value)));
  }

  function formatNumber(value) {
    const rounded = Math.round(finiteNumber(value) * 100) / 100;
    const normalized = Object.is(rounded, -0) ? 0 : rounded;
    return normalized.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  function formatKeyNumber(value) {
    const rounded = Math.round(finiteNumber(value) * 100) / 100;
    return String(Object.is(rounded, -0) ? 0 : rounded);
  }

  function productConfig(product) {
    return PRODUCT_CONFIG[product] || PRODUCT_CONFIG.vinyl;
  }

  function normalizeGate(gate, config) {
    const requestedWidth = finiteNumber(gate && gate.widthFt);
    const widthFt = config.gateSizes.includes(requestedWidth) ? requestedWidth : config.gateSizes[0];
    const rawCount = gate && gate.countInput !== undefined
      ? String(gate.countInput)
      : String(nonNegativeInteger(gate && gate.count) || 1);

    return {
      id: gate && typeof gate.id === 'string' ? gate.id : createId('gate'),
      widthFt: widthFt,
      countInput: rawCount
    };
  }

  function normalizeState(rawState) {
    const product = rawState && PRODUCT_CONFIG[rawState.product] ? rawState.product : 'vinyl';
    const config = productConfig(product);
    const requestedHeight = finiteNumber(rawState && rawState.height);
    const height = config.heights.includes(requestedHeight) ? requestedHeight : config.defaultHeight;
    const segments = Array.isArray(rawState && rawState.segments)
      ? rawState.segments.reduce(function (result, segment) {
          if (!segment || (segment.type !== 'stretch' && segment.type !== 'break')) return result;

          if (segment.type === 'break') {
            result.push({
              id: typeof segment.id === 'string' ? segment.id : createId('break'),
              type: 'break'
            });
            return result;
          }

          result.push({
            id: typeof segment.id === 'string' ? segment.id : createId('stretch'),
            type: 'stretch',
            lengthInput: segment.lengthInput !== undefined
              ? String(segment.lengthInput)
              : (positiveNumber(segment.lengthFt) ? String(segment.lengthFt) : ''),
            gates: Array.isArray(segment.gates)
              ? segment.gates.map(function (gate) { return normalizeGate(gate, config); })
              : []
          });
          return result;
        }, [])
      : [];

    return {
      product: product,
      height: height,
      panelLengthInput: rawState && rawState.panelLengthInput !== undefined
        ? String(rawState.panelLengthInput)
        : String(config.defaultPanelLength),
      postDepthInput: rawState && rawState.postDepthInput !== undefined
        ? String(rawState.postDepthInput)
        : String(config.defaultPostDepth),
      segments: segments
    };
  }

  function loadState(storageKey) {
    try {
      const saved = window.localStorage.getItem(storageKey);
      return normalizeState(saved ? JSON.parse(saved) : null);
    } catch (error) {
      return normalizeState(null);
    }
  }

  function saveState(storageKey, state) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      return;
    }
  }

  function gateSummary(gates) {
    return (gates || []).reduce(function (summary, gate) {
      const widthFt = positiveNumber(gate.widthFt);
      const count = nonNegativeInteger(gate.countInput);
      if (!GATE_SIZES.includes(widthFt) || count <= 0) return summary;

      summary.gateFeet += widthFt * count;
      summary.gateCount += count;
      summary.counts[widthFt] += count;
      if (widthFt <= 6) summary.singles += count;
      else summary.doubles += count;
      return summary;
    }, {
      gateFeet: 0,
      gateCount: 0,
      singles: 0,
      doubles: 0,
      counts: { 4: 0, 5: 0, 6: 0, 8: 0, 10: 0, 12: 0 }
    });
  }

  function calculateStretchPanelMeasurement(stretchLengthFt, panelLengthFt, gateFeet, gateCount) {
    const stretchFeet = positiveNumber(stretchLengthFt);
    const panelFeet = positiveNumber(panelLengthFt);
    const normalizedGateFeet = positiveNumber(gateFeet);
    const normalizedGateCount = nonNegativeInteger(gateCount);

    if (!stretchFeet || !panelFeet) {
      return {
        panelCount: 0,
        fullPanelFeet: 0,
        totalFullPanelLayoutFt: normalizedGateFeet,
        marginFt: stretchFeet - normalizedGateFeet
      };
    }

    const gateFootprintFt = normalizedGateFeet + panelFeet * (2 * normalizedGateCount);
    let panelCount = 0;

    if (normalizedGateCount > 0 && gateFootprintFt >= stretchFeet) {
      panelCount = 2;
    } else if (normalizedGateCount > 0) {
      panelCount = Math.ceil((stretchFeet - normalizedGateFeet) / panelFeet);
    } else {
      panelCount = Math.ceil(stretchFeet / panelFeet);
    }

    const fullPanelFeet = panelCount * panelFeet;
    const totalFullPanelLayoutFt = fullPanelFeet + normalizedGateFeet;

    return {
      panelCount: panelCount,
      fullPanelFeet: fullPanelFeet,
      totalFullPanelLayoutFt: totalFullPanelLayoutFt,
      marginFt: stretchFeet - totalFullPanelLayoutFt
    };
  }

  function calculateStretch(segment, panelLengthFt) {
    const lengthFt = positiveNumber(segment.lengthInput);
    const gates = gateSummary(segment.gates);
    const measurement = calculateStretchPanelMeasurement(
      lengthFt,
      panelLengthFt,
      gates.gateFeet,
      gates.gateCount
    );
    const gateFootprintFt = gates.gateFeet + panelLengthFt * (2 * gates.gateCount);
    let linePosts = 0;

    if (lengthFt > 0) {
      if (gates.gateCount > 0 && gateFootprintFt >= lengthFt) {
        linePosts = 0;
      } else if (gates.gateCount > 0) {
        linePosts = Math.ceil(Math.max(0, lengthFt - gateFootprintFt) / panelLengthFt);
      } else {
        linePosts = Math.max(0, measurement.panelCount - 1);
      }
    }

    return {
      lengthFt: lengthFt,
      panels: measurement.panelCount,
      linePosts: linePosts,
      fullPanelLayoutFt: measurement.totalFullPanelLayoutFt,
      marginFt: measurement.marginFt,
      gateLeafCount: gates.singles + 2 * gates.doubles,
      gates: gates
    };
  }

  function calculateTotals(state) {
    const config = productConfig(state.product);
    const panelLengthFt = positiveNumber(state.panelLengthInput) || config.defaultPanelLength;
    const postDepthFt = positiveNumber(state.postDepthInput) || config.defaultPostDepth;
    const runs = [];
    let currentRun = [];

    state.segments.forEach(function (segment) {
      if (segment.type === 'break') {
        if (currentRun.length) runs.push(currentRun);
        currentRun = [];
        return;
      }

      const calculated = calculateStretch(segment, panelLengthFt);
      if (calculated.lengthFt > 0) currentRun.push(calculated);
    });
    if (currentRun.length) runs.push(currentRun);

    const totals = {
      panelLengthFt: panelLengthFt,
      postDepthFt: postDepthFt,
      totalFeet: 0,
      panels: 0,
      linePosts: 0,
      endPosts: 0,
      cornerPosts: 0,
      totalPosts: 0,
      fullPanelLayoutFt: 0,
      marginFt: 0,
      gateLeafCount: 0,
      gateOpenings: 0,
      gateCounts: { 4: 0, 5: 0, 6: 0, 8: 0, 10: 0, 12: 0 },
      validStretchCount: 0
    };

    runs.forEach(function (run) {
      totals.cornerPosts += Math.max(0, run.length - 1);
      run.forEach(function (stretch) {
        totals.validStretchCount += 1;
        totals.totalFeet += stretch.lengthFt;
        totals.panels += stretch.panels;
        totals.linePosts += stretch.linePosts;
        totals.fullPanelLayoutFt += stretch.fullPanelLayoutFt;
        totals.marginFt += stretch.marginFt;
        totals.gateLeafCount += stretch.gateLeafCount;
        totals.gateOpenings += stretch.gates.gateCount;
        GATE_SIZES.forEach(function (size) {
          totals.gateCounts[size] += stretch.gates.counts[size] || 0;
        });
      });
    });

    totals.endPosts = runs.length * 2 + totals.gateOpenings * 2;
    totals.totalPosts = totals.linePosts + totals.endPosts + totals.cornerPosts;
    return totals;
  }

  function buildMaterialRows(state, totals) {
    if (!totals.validStretchCount) return [];

    const rows = [];
    const postLengthFt = finiteNumber(state.height) + totals.postDepthFt;

    function add(name, quantity) {
      const normalizedQuantity = nonNegativeInteger(quantity);
      if (normalizedQuantity > 0) rows.push({ name: name, quantity: normalizedQuantity });
    }

    if (state.product === 'vinyl') {
      add(formatNumber(totals.panelLengthFt) + '′ long panels', totals.panels);
      add(formatNumber(postLengthFt) + '′ line posts', totals.linePosts);
      add(formatNumber(postLengthFt) + '′ end posts', totals.endPosts);
      add(formatNumber(postLengthFt) + '′ corner posts', totals.cornerPosts);
      add('Gate stiffeners', totals.gateLeafCount);
      GATE_SIZES.forEach(function (size) {
        const gateType = size <= 6 ? 'single gate with hardware' : 'drive gate with hardware';
        add('1 — ' + size + '′ wide ' + gateType, totals.gateCounts[size]);
      });
      add('Exterior caps', totals.totalPosts);
      return rows;
    }

    const fourFootGateLeaves = totals.gateCounts[4] + 2 * totals.gateCounts[8];
    const fiveFootGateLeaves = totals.gateCounts[5] + 2 * totals.gateCounts[10];
    add(formatNumber(totals.panelLengthFt) + '′ W × ' + formatNumber(state.height) + '′ T ornamental panels', totals.panels);
    add('2″ square ornamental posts — ' + formatNumber(postLengthFt) + '′', totals.totalPosts);
    add('Ornamental line brackets — 2″', totals.linePosts * 3);
    add('Ornamental universal brackets — 2″', totals.endPosts * 3 + totals.cornerPosts * 6);
    add('1 — 4′ wide single ornamental gate', fourFootGateLeaves);
    add('1 — 5′ wide single ornamental gate', fiveFootGateLeaves);
    return rows;
  }

  function buildCartPairs(state, totals) {
    const pairs = [];
    const panelLengthKey = formatKeyNumber(totals.panelLengthFt);
    const postLengthKey = formatKeyNumber(finiteNumber(state.height) + totals.postDepthFt);

    function add(key, quantity) {
      const normalizedQuantity = nonNegativeInteger(quantity);
      if (normalizedQuantity > 0) pairs.push({ key: key, quantity: normalizedQuantity });
    }

    if (state.product === 'vinyl') {
      add('VINYL:PANEL:' + panelLengthKey, totals.panels);
      add('VINYL:POST:LINE:' + postLengthKey, totals.linePosts);
      add('VINYL:POST:END:' + postLengthKey, totals.endPosts);
      add('VINYL:POST:CORNER:' + postLengthKey, totals.cornerPosts);
      add('VINYL:GATE:STIFFENER', totals.gateLeafCount);
      add('VINYL:GATE:SINGLE:4', totals.gateCounts[4] + 2 * totals.gateCounts[8]);
      add('VINYL:GATE:SINGLE:5', totals.gateCounts[5] + 2 * totals.gateCounts[10]);
      add('VINYL:GATE:SINGLE:6', totals.gateCounts[6] + 2 * totals.gateCounts[12]);
      add('VINYL:CAP:EXTERIOR', totals.totalPosts);
      return pairs;
    }

    add('ORN:PANEL:' + formatKeyNumber(state.height) + ':' + panelLengthKey, totals.panels);
    add('ORN:POSTS:' + postLengthKey, totals.totalPosts);
    add('ORN:BRACKET:LINE', totals.linePosts * 3);
    add('ORN:BRACKET:UNIVERSAL', totals.endPosts * 3 + totals.cornerPosts * 6);
    add('ORN:GATE:SINGLE:4', totals.gateCounts[4] + 2 * totals.gateCounts[8]);
    add('ORN:GATE:SINGLE:5', totals.gateCounts[5] + 2 * totals.gateCounts[10]);
    return pairs;
  }

  function createOptions(values, selectedValue, suffix) {
    return values.map(function (value) {
      const selected = Number(value) === Number(selectedValue) ? ' selected' : '';
      return '<option value="' + escapeHtml(value) + '"' + selected + '>' +
        escapeHtml(value + suffix) +
        '</option>';
    }).join('');
  }

  function initCalculator(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const storageKey = root.dataset.storageKey || 'cyclone-stretch-calculator-v2';
    const elements = {
      product: root.querySelector('[data-field="product"]'),
      height: root.querySelector('[data-field="height"]'),
      panelLength: root.querySelector('[data-field="panel-length"]'),
      postDepth: root.querySelector('[data-field="post-depth"]'),
      segments: root.querySelector('[data-segments]'),
      bottomActions: root.querySelector('[data-bottom-actions]'),
      materialEmpty: root.querySelector('[data-material-empty]'),
      materialTableWrap: root.querySelector('[data-material-table-wrap]'),
      materialRows: root.querySelector('[data-material-rows]'),
      cartButton: root.querySelector('[data-action="add-cart"]'),
      cartButtonLabel: root.querySelector('[data-cart-button-label]'),
      status: root.querySelector('[data-status]'),
      summaryFeet: root.querySelector('[data-summary="feet"]'),
      summaryPanels: root.querySelector('[data-summary="panels"]'),
      summaryPosts: root.querySelector('[data-summary="posts"]'),
      summaryGates: root.querySelector('[data-summary="gates"]')
    };
    let state = loadState(storageKey);
    let cartBusy = false;

    function config() {
      return productConfig(state.product);
    }

    function setStatus(message, tone) {
      if (!elements.status) return;
      elements.status.textContent = message || '';
      if (tone) elements.status.dataset.tone = tone;
      else delete elements.status.dataset.tone;
    }

    function persist() {
      saveState(storageKey, state);
    }

    function findStretch(stretchId) {
      return state.segments.find(function (segment) {
        return segment.type === 'stretch' && segment.id === stretchId;
      });
    }

    function renderControls() {
      const activeConfig = config();
      elements.product.value = state.product;
      elements.height.innerHTML = createOptions(activeConfig.heights, state.height, ' ft');
      elements.panelLength.value = state.panelLengthInput;
      elements.postDepth.value = state.postDepthInput;
    }

    function gateMarkup(stretch, gate, gateIndex) {
      return [
        '<div class="fc-gate" data-gate-id="', escapeHtml(gate.id), '">',
          '<div class="fc-gate-name">Gate ', String(gateIndex + 1), '</div>',
          '<label class="fc-field">',
            '<span>Width (ft)</span>',
            '<select data-input="gate-width" data-stretch-id="', escapeHtml(stretch.id), '" data-gate-id="', escapeHtml(gate.id), '">',
              createOptions(config().gateSizes, gate.widthFt, '′'),
            '</select>',
          '</label>',
          '<label class="fc-field">',
            '<span>Quantity</span>',
            '<input type="number" min="0" step="1" inputmode="numeric" value="', escapeHtml(gate.countInput), '" data-input="gate-count" data-stretch-id="', escapeHtml(stretch.id), '" data-gate-id="', escapeHtml(gate.id), '">',
          '</label>',
          '<button class="fc-icon-button" type="button" data-action="remove-gate" data-stretch-id="', escapeHtml(stretch.id), '" data-gate-id="', escapeHtml(gate.id), '">Remove</button>',
        '</div>'
      ].join('');
    }

    function stretchMarkup(stretch, stretchNumber) {
      const gates = stretch.gates.map(function (gate, gateIndex) {
        return gateMarkup(stretch, gate, gateIndex);
      }).join('');

      return [
        '<article class="fc-stretch" data-stretch-id="', escapeHtml(stretch.id), '">',
          '<div class="fc-stretch-header">',
            '<div class="fc-stretch-title"><span class="fc-stretch-number">', String(stretchNumber), '</span>Stretch ', String(stretchNumber), '</div>',
            '<button class="fc-icon-button" type="button" data-action="remove-stretch" data-stretch-id="', escapeHtml(stretch.id), '">Remove Stretch</button>',
          '</div>',
          '<div class="fc-stretch-body">',
            '<div class="fc-stretch-input-row">',
              '<label class="fc-field">',
                '<span>Length (ft)</span>',
                '<input type="number" min="0" step="0.1" inputmode="decimal" value="', escapeHtml(stretch.lengthInput), '" data-input="stretch-length" data-stretch-id="', escapeHtml(stretch.id), '">',
              '</label>',
              '<div class="fc-measurements" data-measurements="', escapeHtml(stretch.id), '">',
                '<div class="fc-measurement"><span>Panels</span><strong data-measurement="panels">0</strong></div>',
                '<div class="fc-measurement"><span>Full-panel length</span><strong data-measurement="layout">0 ft</strong></div>',
                '<div class="fc-measurement"><span>Margin</span><strong data-measurement="margin">0 ft</strong></div>',
              '</div>',
            '</div>',
            '<div class="fc-gates">',
              '<div class="fc-gates-heading">',
                '<strong>Gates on this stretch</strong>',
                '<button class="fc-button fc-button-secondary fc-add-gate" type="button" data-action="add-gate" data-stretch-id="', escapeHtml(stretch.id), '"><span aria-hidden="true">+</span>Add Gate</button>',
              '</div>',
              '<div class="fc-gate-list">', gates, '</div>',
            '</div>',
          '</div>',
        '</article>'
      ].join('');
    }

    function breakMarkup(segment) {
      return [
        '<div class="fc-break">',
          '<div class="fc-break-content">',
            '<strong>Break</strong>',
            '<span>Next stretch starts a separate run</span>',
            '<button type="button" data-action="remove-break" data-break-id="', escapeHtml(segment.id), '">Remove</button>',
          '</div>',
        '</div>'
      ].join('');
    }

    function renderSegments() {
      if (!state.segments.length) {
        elements.segments.innerHTML = [
          '<div class="fc-empty-state">',
            '<strong>No stretches added yet</strong>',
            '<span>Add the first stretch, enter its length, then add any gates located on that stretch.</span>',
          '</div>'
        ].join('');
        elements.bottomActions.hidden = true;
        return;
      }

      let stretchNumber = 0;
      elements.segments.innerHTML = state.segments.map(function (segment) {
        if (segment.type === 'break') return breakMarkup(segment);
        stretchNumber += 1;
        return stretchMarkup(segment, stretchNumber);
      }).join('');
      elements.bottomActions.hidden = false;
    }

    function updateMeasurements() {
      const activeConfig = config();
      const panelLengthFt = positiveNumber(state.panelLengthInput) || activeConfig.defaultPanelLength;

      state.segments.forEach(function (segment) {
        if (segment.type !== 'stretch') return;
        const measurementRoot = elements.segments.querySelector('[data-measurements="' + CSS.escape(segment.id) + '"]');
        if (!measurementRoot) return;

        const calculated = calculateStretch(segment, panelLengthFt);
        const panels = measurementRoot.querySelector('[data-measurement="panels"]');
        const layout = measurementRoot.querySelector('[data-measurement="layout"]');
        const margin = measurementRoot.querySelector('[data-measurement="margin"]');
        panels.textContent = formatNumber(calculated.panels);
        layout.textContent = formatNumber(calculated.fullPanelLayoutFt) + ' ft';
        margin.textContent = formatNumber(calculated.marginFt) + ' ft';
      });
    }

    function updateOutputs() {
      const totals = calculateTotals(state);
      const rows = buildMaterialRows(state, totals);

      elements.summaryFeet.textContent = formatNumber(totals.totalFeet) + ' ft';
      elements.summaryPanels.textContent = formatNumber(totals.panels);
      elements.summaryPosts.textContent = formatNumber(totals.totalPosts);
      elements.summaryGates.textContent = formatNumber(totals.gateOpenings);

      if (rows.length) {
        elements.materialRows.innerHTML = rows.map(function (row) {
          return '<tr><td>' + escapeHtml(row.name) + '</td><td>' + escapeHtml(row.quantity) + '</td></tr>';
        }).join('');
        elements.materialEmpty.hidden = true;
        elements.materialTableWrap.hidden = false;
      } else {
        elements.materialRows.innerHTML = '';
        elements.materialEmpty.hidden = false;
        elements.materialTableWrap.hidden = true;
      }

      if (elements.cartButton) {
        elements.cartButton.disabled = cartBusy || !rows.length;
      }
      updateMeasurements();
      persist();
    }

    function renderAll() {
      renderControls();
      renderSegments();
      updateOutputs();
    }

    function addStretch() {
      state.segments.push({
        id: createId('stretch'),
        type: 'stretch',
        lengthInput: '',
        gates: []
      });
      setStatus('');
      renderSegments();
      updateOutputs();

      const newestStretch = state.segments[state.segments.length - 1];
      const input = elements.segments.querySelector('[data-input="stretch-length"][data-stretch-id="' + CSS.escape(newestStretch.id) + '"]');
      if (input) input.focus();
    }

    function addBreak() {
      const lastSegment = state.segments[state.segments.length - 1];
      if (!lastSegment || lastSegment.type !== 'stretch') {
        setStatus('Add a stretch before adding a break.', 'error');
        return;
      }

      state.segments.push({ id: createId('break'), type: 'break' });
      setStatus('');
      renderSegments();
      updateOutputs();
    }

    function removeSegment(id, type) {
      state.segments = state.segments.filter(function (segment) {
        return !(segment.type === type && segment.id === id);
      });
      state.segments = state.segments.filter(function (segment, index, segments) {
        if (segment.type !== 'break') return true;
        return index > 0 &&
          index < segments.length - 1 &&
          segments[index - 1].type === 'stretch' &&
          segments[index + 1].type === 'stretch';
      });
      setStatus('');
      renderSegments();
      updateOutputs();
    }

    function addGate(stretchId) {
      const stretch = findStretch(stretchId);
      if (!stretch) return;
      stretch.gates.push({
        id: createId('gate'),
        widthFt: config().gateSizes[0],
        countInput: '1'
      });
      setStatus('');
      renderSegments();
      updateOutputs();
    }

    function removeGate(stretchId, gateId) {
      const stretch = findStretch(stretchId);
      if (!stretch) return;
      stretch.gates = stretch.gates.filter(function (gate) { return gate.id !== gateId; });
      setStatus('');
      renderSegments();
      updateOutputs();
    }

    function resetCalculator() {
      const hasEnteredData = state.segments.some(function (segment) {
        return segment.type === 'stretch' && (positiveNumber(segment.lengthInput) > 0 || segment.gates.length > 0);
      });
      if (hasEnteredData && !window.confirm('Reset the entire stretch calculator?')) return;

      state = normalizeState(null);
      setStatus('');
      renderAll();
    }

    function updateProduct(nextProduct) {
      if (!PRODUCT_CONFIG[nextProduct]) return;
      const nextConfig = productConfig(nextProduct);
      state.product = nextProduct;
      state.height = nextConfig.defaultHeight;
      state.panelLengthInput = String(nextConfig.defaultPanelLength);
      state.postDepthInput = String(nextConfig.defaultPostDepth);
      state.segments.forEach(function (segment) {
        if (segment.type !== 'stretch') return;
        segment.gates = segment.gates.map(function (gate) {
          return normalizeGate(gate, nextConfig);
        });
      });
      setStatus('');
      renderAll();
    }

    function updateGateValue(stretchId, gateId, key, value) {
      const stretch = findStretch(stretchId);
      if (!stretch) return;
      const gate = stretch.gates.find(function (candidate) { return candidate.id === gateId; });
      if (!gate) return;
      gate[key] = value;
      setStatus('');
      updateOutputs();
    }

    function missingSkus(skuList) {
      return Array.from(new Set(skuList.filter(Boolean))).filter(function (sku) {
        return !variantIdBySku[sku];
      });
    }

    async function resolveSkuList(skuList) {
      let unresolved = missingSkus(skuList);
      if (!unresolved.length) return;

      for (let page = 1; page <= 10 && unresolved.length; page += 1) {
        let response;
        try {
          response = await fetch('/products.json?limit=250&page=' + page, { credentials: 'same-origin' });
        } catch (error) {
          break;
        }
        if (!response.ok) break;

        const data = await response.json();
        const products = data && (data.products || data) || [];
        if (!products.length) break;

        products.forEach(function (product) {
          (product.variants || []).forEach(function (variant) {
            const sku = String(variant.sku || '').trim();
            if (sku && unresolved.includes(sku)) variantIdBySku[sku] = variant.id;
          });
        });
        unresolved = missingSkus(unresolved);
      }

      for (const sku of unresolved.slice()) {
        try {
          const suggestResponse = await fetch(
            '/search/suggest.json?q=' + encodeURIComponent(sku) + '&resources[type]=product&resources[limit]=10',
            { credentials: 'same-origin' }
          );
          if (!suggestResponse.ok) continue;
          const suggestions = await suggestResponse.json();
          const products = suggestions &&
            suggestions.resources &&
            suggestions.resources.results &&
            suggestions.resources.results.products || [];

          for (const product of products) {
            const handle = product.handle || String(product.url || '').split('/products/')[1];
            if (!handle) continue;
            const productResponse = await fetch('/products/' + handle + '.js', { credentials: 'same-origin' });
            if (!productResponse.ok) continue;
            const productJson = await productResponse.json();
            const matchedVariant = (productJson.variants || []).find(function (variant) {
              return String(variant.sku || '').trim() === sku;
            });
            if (matchedVariant) {
              variantIdBySku[sku] = matchedVariant.id;
              break;
            }
          }
        } catch (error) {
          continue;
        }
      }
    }

    function mapPairsToSkuItems(pairs) {
      const missingMappings = [];
      const skuItems = [];

      pairs.forEach(function (pair) {
        const mapping = SKU_MAP[pair.key];
        if (!mapping) {
          missingMappings.push(pair.key);
          return;
        }

        const sku = typeof mapping === 'string'
          ? mapping
          : mapping[state.height];
        if (!sku) {
          missingMappings.push(pair.key);
          return;
        }
        skuItems.push({ sku: sku, quantity: pair.quantity });
      });

      return { missingMappings: missingMappings, skuItems: skuItems };
    }

    async function addAllToCart() {
      if (cartBusy) return;
      const totals = calculateTotals(state);
      if (!totals.validStretchCount) {
        setStatus('Enter a stretch length before adding materials to the cart.', 'error');
        return;
      }

      const mapped = mapPairsToSkuItems(buildCartPairs(state, totals));
      if (mapped.missingMappings.length) {
        setStatus(
          'The additional settings do not match the standard products sold in the store. Use the standard 8′ panel and post-depth settings before adding to cart.',
          'error'
        );
        return;
      }

      cartBusy = true;
      elements.cartButton.disabled = true;
      const originalLabel = elements.cartButtonLabel.textContent;
      elements.cartButtonLabel.textContent = 'Adding materials…';
      setStatus('');

      try {
        await resolveSkuList(mapped.skuItems.map(function (item) { return item.sku; }));
        const unresolved = missingSkus(mapped.skuItems.map(function (item) { return item.sku; }));
        if (unresolved.length) {
          throw new Error('Products could not be found for SKUs: ' + unresolved.join(', '));
        }

        const quantitiesByVariantId = Object.create(null);
        mapped.skuItems.forEach(function (item) {
          const variantId = variantIdBySku[item.sku];
          quantitiesByVariantId[variantId] = (quantitiesByVariantId[variantId] || 0) + item.quantity;
        });

        const items = Object.keys(quantitiesByVariantId).map(function (variantId) {
          return {
            id: Number(variantId),
            quantity: quantitiesByVariantId[variantId]
          };
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!response.ok) throw new Error('Shopify rejected the cart update.');

        setStatus('Materials added. Opening your cart…', 'success');
        window.location.assign('/cart');
      } catch (error) {
        console.error('[Cyclone stretch calculator]', error);
        setStatus('The materials could not be added to the cart. Please refresh the page and try again.', 'error');
      } finally {
        cartBusy = false;
        elements.cartButtonLabel.textContent = originalLabel;
        updateOutputs();
      }
    }

    root.addEventListener('click', function (event) {
      const button = event.target.closest('[data-action]');
      if (!button || !root.contains(button)) return;
      const action = button.dataset.action;

      if (action === 'add-stretch') addStretch();
      else if (action === 'add-break') addBreak();
      else if (action === 'reset') resetCalculator();
      else if (action === 'remove-stretch') removeSegment(button.dataset.stretchId, 'stretch');
      else if (action === 'remove-break') removeSegment(button.dataset.breakId, 'break');
      else if (action === 'add-gate') addGate(button.dataset.stretchId);
      else if (action === 'remove-gate') removeGate(button.dataset.stretchId, button.dataset.gateId);
      else if (action === 'add-cart') addAllToCart();
    });

    root.addEventListener('input', function (event) {
      const target = event.target;
      const field = target.dataset.field;
      const input = target.dataset.input;

      if (field === 'panel-length') {
        state.panelLengthInput = target.value;
        setStatus('');
        updateOutputs();
      } else if (field === 'post-depth') {
        state.postDepthInput = target.value;
        setStatus('');
        updateOutputs();
      } else if (input === 'stretch-length') {
        const stretch = findStretch(target.dataset.stretchId);
        if (!stretch) return;
        stretch.lengthInput = target.value;
        setStatus('');
        updateOutputs();
      } else if (input === 'gate-count') {
        updateGateValue(target.dataset.stretchId, target.dataset.gateId, 'countInput', target.value);
      }
    });

    root.addEventListener('change', function (event) {
      const target = event.target;
      const field = target.dataset.field;
      const input = target.dataset.input;

      if (field === 'product') updateProduct(target.value);
      else if (field === 'height') {
        state.height = finiteNumber(target.value);
        setStatus('');
        updateOutputs();
      } else if (input === 'gate-width') {
        updateGateValue(target.dataset.stretchId, target.dataset.gateId, 'widthFt', finiteNumber(target.value));
      }
    });

    renderAll();
  }

  function initializeAll(scope) {
    const searchRoot = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    if (searchRoot.matches && searchRoot.matches(ROOT_SELECTOR)) initCalculator(searchRoot);
    searchRoot.querySelectorAll(ROOT_SELECTOR).forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(document); });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
